import { useQuery, useMutation } from '@tanstack/react-query';
import { UserApi } from '@Apis';
import { TargetType } from '@Utils/enums';
import { Service } from '@Types/app';
import { TreeNode } from '@Components/Tree/type';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TeamsTarget {
  id: string;
  name: string;
  displayName?: string;
  url?: string;
  email?: string;
  type?: string;
  title?: string;
  key?: string;
}

interface PaginationInfo {
  userOffset: number;
  userTotalCount: number;
  groupOffset: number;
  groupTotalCount: number;
  next: number;
}

interface TeamsTargetsResponse {
  targets: TeamsTarget[];
  targetGroups: TeamsTarget[];
  pagination?: PaginationInfo;
}

const getTeamsTargetsQueryKey = (orgId: string, searchString?: string) => [
  'teamsTargets',
  orgId,
  searchString,
];

// Hook to manage Teams targets with pagination and search
export const useTeamsTargets = (
  orgId: string,
  enabled = true,
  searchString?: string
) => {
  const { t } = useTranslation();
  const [allTargets, setAllTargets] = useState<TeamsTarget[]>([]);
  const [allGroups, setAllGroups] = useState<TeamsTarget[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreTargets, setHasMoreTargets] = useState(false);
  const [hasMoreGroups, setHasMoreGroups] = useState(false);

  // Initial query to get first page
  const initialQuery = useQuery({
    queryKey: getTeamsTargetsQueryKey(orgId, searchString),
    queryFn: async () => {
      const service = Service.Teams;
      const targetType = TargetType.UserAndGroup;

      // Use search API if searchString is provided, otherwise use regular API
      if (searchString && searchString.trim()) {
        const response = await UserApi.searchTarget(
          searchString.trim(),
          orgId,
          service,
          targetType,
          undefined,
          undefined
        );
        return response.data as TeamsTargetsResponse;
      } else {
        // First request without pagination data
        const response = await UserApi.getPolicyTargets(
          orgId,
          service,
          targetType,
          '0',
          undefined,
          undefined
        );
        return response.data as TeamsTargetsResponse;
      }
    },
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to load more data
  const loadMoreMutation = useMutation({
    mutationFn: async (paginationData: PaginationInfo) => {
      const service = Service.Teams;
      const targetType = TargetType.UserAndGroup;

      // Use search API if searchString is provided, otherwise use regular API
      if (searchString && searchString.trim()) {
        const response = await UserApi.searchTarget(
          searchString.trim(),
          orgId,
          service,
          targetType,
          paginationData,
          undefined
        );
        return response.data as TeamsTargetsResponse;
      } else {
        // Subsequent requests with pagination data
        const response = await UserApi.getPolicyTargets(
          orgId,
          service,
          targetType,
          '0',
          paginationData,
          undefined
        );
        return response.data as TeamsTargetsResponse;
      }
    },
    onSuccess: (data) => {
      if (data.targets) {
        setAllTargets((prev) => [...prev, ...data.targets]);
      }
      if (data.targetGroups) {
        setAllGroups((prev) => [...prev, ...data.targetGroups]);
      }
      if (data.pagination) {
        setPagination(data.pagination);
        updateHasMore(data.pagination);
      } else {
        // No pagination means we've reached the end
        setHasMoreTargets(false);
        setHasMoreGroups(false);
      }
      setIsLoadingMore(false);
    },
    onError: () => {
      setIsLoadingMore(false);
    },
  });

  // Update initial data when query succeeds
  useEffect(() => {
    if (initialQuery.data) {
      setAllTargets(initialQuery.data.targets || []);
      setAllGroups(initialQuery.data.targetGroups || []);
      if (initialQuery.data.pagination) {
        setPagination(initialQuery.data.pagination);
        updateHasMore(initialQuery.data.pagination);
      }
    }
  }, [initialQuery.data]);

  const updateHasMore = (paginationInfo: PaginationInfo) => {
    setHasMoreTargets(
      paginationInfo.userOffset < paginationInfo.userTotalCount
    );
    setHasMoreGroups(
      paginationInfo.groupOffset < paginationInfo.groupTotalCount
    );
  };

  // Load more function
  const loadMore = useCallback(() => {
    if (pagination && !isLoadingMore && (hasMoreTargets || hasMoreGroups)) {
      setIsLoadingMore(true);
      loadMoreMutation.mutate(pagination);
    }
  }, [
    pagination,
    isLoadingMore,
    hasMoreTargets,
    hasMoreGroups,
    loadMoreMutation,
  ]);

  // Transform to tree nodes — each team (target or group) becomes a selectable leaf
  const transformToTreeNodes = useCallback((): TreeNode[] => {
    const allTeamsChildren: TreeNode[] = [];

    const pushTeam = (team: TeamsTarget) => {
      allTeamsChildren.push({
        key: team.key || team.id || team.email || team.url || team.name,
        title: team.name || team.displayName || team.title || 'Team',
        isLeaf: true,
        data: team,
      });
    };

    allTargets.forEach(pushTeam);
    allGroups.forEach(pushTeam);

    // Add "Show more" node if there are more items
    if (hasMoreTargets || hasMoreGroups) {
      allTeamsChildren.push({
        key: 'show-more',
        title: isLoadingMore
          ? t('common.text.loading')
          : t('policy.node.showMore'),
        isLeaf: true,
        disableCheckbox: true,
        onSelect: loadMore,
        isShowMore: true,
      });
    }

    return [
      {
        key: 'all-teams',
        title: t('common.service.teams'),
        children: allTeamsChildren,
        expanded: true,
      },
    ];
  }, [
    allTargets,
    allGroups,
    hasMoreTargets,
    hasMoreGroups,
    isLoadingMore,
    t,
    loadMore,
  ]);

  return {
    data: transformToTreeNodes(),
    isLoading: initialQuery.isLoading,
    isLoadingMore,
    error: initialQuery.error,
    refetch: initialQuery.refetch,
    loadMore,
  };
};
